import { ModalCreateEditMedium } from '../../../../../../baseComponents/ModalsLayouts/ModalCreateEdit'
import { ModalEditComponentProps } from '../../../../../../baseComponents/ModalsLayouts/types'
import useTwinTranslation from '../../../../../../utils/hooks/useTwinTranslation'
import { BeforeSubmitHandler, OnSubmit } from '../../../../../../forms/TwinForm/types'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { ColumnTableSchema } from '../../../../../../baseComponents/TwinTable/VirtualTableListing/Subcomponents/types'
import VirtualTableListing from '../../../../../../baseComponents/TwinTable/VirtualTableListing'
import { ProductAddonWithLang } from '../type'
import { twinFetchPostJSON } from '../../../../../../utils/globals/data'
import { dictionaryComplexFromJsonArr, TwinDictionary } from '../../../../../../utils/globals/dictionary'
import { getAllSpecificPriceRules } from '../../../../../../utils/reducers/getters'
import InputQuantity from '../../../../../../forms/Input/InputQuantity'
import { ProductAddonRelModelType } from '@teinor/erp/types/company/product/productAddonRel'
import {  excludeProductAddonId, parseScaleValue, parseSellVariables, sortProductAddons } from './functions'
import { CheckboxMargin } from '../../../../../../forms/Checkbox'
import withLoading from '../../../../../../utils/hoc/withLoading'
import { CustomSelectSectionsSearchbar } from '../../../../../../forms/CustomSelect/CustomSelectSections'
import { CustomSelectSectionItems } from '../../../../../../forms/CustomSelect/CustomSelectSections/types'
import { SellVariablePayload } from '../../../../../../utils/reducers/company/sellVariables'
import { connect, ConnectedProps } from 'react-redux'
import { AllReduxPayloads } from '../../../../../../utils/reducers'
import './modalUpdateProductAddons.sass'

interface ModalUpdateProductAddonsProps extends ModalUpdateProductAddonsLogicProps {
    url: string
 }

const ModalUpdateProductAddons: React.FC<ModalUpdateProductAddonsProps & ReduxSellVariables> = ({ urlGetData, url, setOpened, onSubmit, ...rest }) => {
    const { t } = useTwinTranslation()
    const { beforeSubmit, onSubmitHandler, data, memorizedItems, myOnChange, myOnChangeCustomSelect } = useModalUpdateProductAddonsLogic({ urlGetData, setOpened, onSubmit, ...rest })
    const columns: ColumnTableSchema<ProductAddonWithLang> = [
        {
            id: 'ProductAddonId',
            dataKey: 'ProductAddonId',
            label: t('id', 'ID'),
            width: 75
        },
        {
            id: 'name',
            dataKey: 'name',
            label: t('name', 'Nombre'),
        },
        {
            id: 'ScaleByProductId',
            dataKey: 'ScaleByProductId',
            label: t('type', 'Tipo'),
            customRender: (parameterValue, allRowData) => <CustomSelectSectionsSearchbar value={parseScaleValue(allRowData)} label='' items={excludeProductAddonId(memorizedItems, allRowData?.ProductAddonId)} className='w-100' fieldName='name' onChange={(val) => myOnChangeCustomSelect(allRowData?.rowIndex, val)} sortFunction={(items) => sortProductAddons(items, rest.allRowData?.id)} />
        },
        {
            id: 'multiplyByWeight',
            dataKey: 'multiplyByWeight',
            label: t('mByWeight', 'M. por peso'),
            toolTip: t('multiplyByWeight', 'Multiplicar por peso'),
            customRender: (value, allRowData) => <div className='flex flex-auto justify-center'> <CheckboxMargin checked={String(value) === 'true' ? true : false} onChange={(val) => myOnChange(allRowData?.rowIndex, val, 'multiplyByWeight')} label={''} /></div>,
        },
        {
            id: 'createDefault',
            dataKey: 'createDefault',
            label: t('create', 'Crear'),
            customRender: (value, allRowData) => <div className='flex flex-auto justify-center'> <CheckboxMargin checked={String(value) === 'true' ? true : false} onChange={(val) => myOnChange(allRowData?.rowIndex, val, 'createDefault')} label={''} /></div>,
            width: 50
        },
        {
            id: 'scaleQuantity',
            dataKey: 'scaleQuantity',
            label: t('quantity', 'Cantidad'),
            customRender: (value, allRowData) => <InputQuantity typeQuantity={''} value={value ? value : ''} onChange={(val) => myOnChange(allRowData?.rowIndex, val, 'scaleQuantity')}/>
        }
    ]
    return (
        <ModalCreateEditMedium setOpened={setOpened} url={url} onSubmit={onSubmitHandler} translations={{ title: t('updateRelations', 'Editar relaciones'), button: t('save', 'Guardar') }} haveButtonPermissions={true} fields={[]} beforeSubmit={beforeSubmit}>
            <div className='flex-auto table_container mt-20 table_modal_update_product_addons'>
                <VirtualTableListing tableData={data} columns={columns} name='modalUpdateProductAddons' />
            </div>
        </ModalCreateEditMedium>
    )
}

interface ModalUpdateProductAddonsLogicProps extends ModalEditComponentProps {
    sellVariables: SellVariablePayload
    urlGetData: string
    field: string
}

const useModalUpdateProductAddonsLogic = ({ field, urlGetData, onSubmit, setOpened, allRowData, sellVariables }: ModalUpdateProductAddonsLogicProps) => {
    const [data, setData] = useState<TwinDictionary[] | null>(null)
    const { t } = useTwinTranslation()
    const manualText = t('manual', 'Manual')
    const productText = t('products', 'Productos')
    const variableText = t('variables', 'Variables')

    const memorizedItems: CustomSelectSectionItems = useMemo(() => {
        const dictData = dictionaryComplexFromJsonArr(data || [], "ProductAddonId")
        const items: TwinDictionary = { 0: { name: manualText, value: 0, ProductAddonId: 0 } }
        if (allRowData?.id) {
            const parsedId = parseInt(String(allRowData.id))
            items[parsedId] = { name: allRowData?.productName || '', value: parsedId, ProductAddonId: parsedId }
        }
        return [
            { name: productText, childs: { ...items, ...dictData } as TwinDictionary },
            { name: variableText, childs: parseSellVariables(sellVariables) }
        ]
    }, [data, manualText, allRowData?.id, allRowData?.productName, sellVariables, productText, variableText])

    const defaultSpecificPriceRuleId = useMemo(() => {
        const specificPriceRules = getAllSpecificPriceRules()
        const specificPriceRulesSorted = specificPriceRules?.arrSorted || []
        return specificPriceRulesSorted?.[0]?.id
    }, [])

    const getData = useCallback(async () => {
        const result = await twinFetchPostJSON(urlGetData, {
            where: {
                [field]: allRowData?.id,
                SpecificPriceRuleId: defaultSpecificPriceRuleId
            },
            order: [['order', 'asc']]
        })
        if (result) {
            setData(result)
        }
    }, [setData, defaultSpecificPriceRuleId, allRowData?.id, urlGetData, field])

    const myOnChange = useCallback((rowIndex: number, value: string | boolean, field: 'scaleQuantity' | 'createDefault' | 'multiplyByWeight') => {
        setData((old) => {
            if (!old) {
                return null
            }
            const copyOld = JSON.parse(JSON.stringify(old))
            copyOld[rowIndex][field] = value
            return copyOld
        })
    }, [setData])

    const myOnChangeCustomSelect = useCallback((rowIndex: number, value: string) => {
        setData((old) => {
            if (!old) {
                return null
            }
            const copyOld = JSON.parse(JSON.stringify(old))
            if (!copyOld[rowIndex]['scaleQuantity']) {
                copyOld[rowIndex]['scaleQuantity'] = 1
            }
            let valueStr = String(value)
            if (valueStr.includes('var_')) {
                const splitted = value.split('var_')
                valueStr = splitted[1]
                copyOld[rowIndex]['ScaleByVariableId'] = valueStr
                copyOld[rowIndex]['ScaleByProductId'] = null
            } else {
                copyOld[rowIndex]['ScaleByProductId'] = valueStr
                if (valueStr === "0") {
                    copyOld[rowIndex]['ScaleByProductId'] = null
                    copyOld[rowIndex]['scaleQuantity'] = null
                }
                copyOld[rowIndex]['ScaleByVariableId'] = null
            }
            return copyOld
        })
    }, [setData])

    const onSubmitHandler: OnSubmit = useCallback((res, values) => {
        onSubmit(res, values)
        setOpened(null)
    }, [onSubmit, setOpened])

    const beforeSubmit: BeforeSubmitHandler = useCallback((vals) => {
        const productAddons: ProductAddonRelModelType[] = []
        if (data) {
            for (const productAddon of data) {
                const { ProductAddonId, ProductId, ScaleByProductId, ScaleByVariableId, scaleQuantity, createDefault, order, multiplyByWeight } = productAddon
                productAddons.push({
                    ProductAddonId,
                    ProductId,
                    ScaleByProductId: ScaleByProductId || null,
                    ScaleByVariableId: ScaleByVariableId || null,
                    scaleQuantity: parseFloat(scaleQuantity),
                    createDefault,
                    multiplyByWeight,
                    order
                })
            }
        }
        vals.productAddons = JSON.stringify(productAddons)
        return true
    }, [data])

    useEffect(() => {
        getData()
    }, [getData])

    return { data, beforeSubmit, onSubmitHandler, memorizedItems, myOnChange, myOnChangeCustomSelect }
}

const sellVariablesDispatch = {
    setSellVariables: (payload: SellVariablePayload) => ({ type: 'CHANGE_SELLVARIABLE', payload }),
}

export type ReduxSellVariables = ConnectedProps<typeof sellVariablesConnect>
const mapSellVariablesConnector = (state: AllReduxPayloads) => ({ sellVariables: state.sellVariables })
const sellVariablesConnect = connect(mapSellVariablesConnector, sellVariablesDispatch)
const sellVariablesConnectLoading = withLoading(ModalUpdateProductAddons, [{ fetchUrl: '/api/app/config/sell/sellVariable/getAllSellVariables', propName: 'sellVariables', setFunctionName: 'setSellVariables' }])
const ModalUpdateProductAddonsConnect = sellVariablesConnect(sellVariablesConnectLoading)

export default ModalUpdateProductAddonsConnect